<template>
  <div class="limit-progress" :class="{ 'limit-progress--compact': compact }" data-testid="limit-progress">
    <!-- Upgrade Button -->
    <button class="upgrade-button" @click="handleUpgrade" data-testid="limit-progress-upgrade">
      <span class="upgrade-text">Upgrade</span>
      <img :src="upgradeCrown" alt="" class="crown-icon" />
    </button>

    <!-- Progress Section -->
    <div class="progress-section">
      <!-- Label and Count -->
      <div class="limit-progress__summary">
        <span class="limit-progress__label">{{ compact ? label : label.toUpperCase() }}</span>
        <span class="limit-progress__count" :class="getCountClass()">{{ current }}/{{ max }}</span>
      </div>

      <!-- Progress Bar -->
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: `${percentage}%` }" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import upgradeCrown from './upgrade-crown.svg';

interface Props {
  label: string;
  current: number;
  max: number;
  onUpgrade?: () => void;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), { compact: false });

const emit = defineEmits<{
  upgrade: [];
}>();

const percentage = computed(() => {
  if (props.max === 0) return 0;
  return Math.min((props.current / props.max) * 100, 100);
});

const getCountClass = () => {
  return 'text-orange-500 dark:text-orange-400';
};

const handleUpgrade = () => {
  if (props.onUpgrade) {
    props.onUpgrade();
  }
  emit('upgrade');
};
</script>

<style scoped>
.limit-progress {
  @apply flex items-center gap-3 sm:gap-5 w-full;
}

/* Upgrade button - same styling as LimitsBanner */
.upgrade-button {
  @apply relative h-9 rounded-xl bg-orange-200 dark:bg-orange-800 flex items-center justify-center gap-1 px-3 py-2 transition-all active:scale-95 cursor-pointer flex-shrink-0;
}

/* Hover state overlay */
.upgrade-button::before {
  @apply absolute content-[''] inset-0 rounded-xl opacity-0 transition-opacity bg-black/10 dark:bg-white/10;
}

.upgrade-button:hover::before {
  @apply opacity-100;
}

.upgrade-button .upgrade-text {
  @apply relative text-xs leading-[18px] font-semibold bg-gradient-to-r from-[#E4983E] to-[#EB6C57] bg-clip-text text-transparent;
}

.crown-icon {
  @apply relative w-[18px] h-[18px] flex-shrink-0;
}

/* Progress section */
.progress-section {
  @apply flex-1 min-w-0;
}

.limit-progress__summary {
  @apply flex items-center justify-between mb-1.5 gap-2;
}

.limit-progress__label,
.limit-progress__count {
  @apply text-sm font-semibold;
}

.limit-progress__label {
  @apply text-zinc-500 dark:text-zinc-400;
}

.limit-progress--compact {
  @apply flex-wrap sm:gap-3;
}

.limit-progress--compact .progress-section {
  flex-basis: 140px;
  min-width: 140px;
}

.limit-progress--compact .limit-progress__label {
  @apply text-[12px] leading-4 font-normal whitespace-nowrap;
}

.limit-progress--compact .limit-progress__count {
  @apply text-[12px] leading-4 whitespace-nowrap;
}

/* Progress track and fill */
.progress-track {
  @apply w-full h-1 rounded-full bg-zinc-100 dark:bg-zinc-700 overflow-hidden;
}

.progress-fill {
  @apply h-full rounded-full transition-all duration-300 ease-in-out bg-orange-500 dark:bg-orange-400;
}
</style>
